import loadScript from 'load-script';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import httpClient from 'superagent';

import { TableStencil } from 'app/components/shared/Table';

import { CONTENT_PROVIDER_IDS } from 'app/constants/BibConstants';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import MoreFromProviderContainer from './MoreFromProviderContainer';

const NOVELIST_CONTENT_API_URL = 'https://novselect.ebscohost.com/Data/ContentByQuery';
const NOVELIST_SCRIPT_SRC = 'https://imageserver.ebscohost.com/novelistselect/ns2init.js';

const MoreFromNoveListContainer = ({ className, metadataId, showHeading }) => {
  const bibs = useSelector(selectBibEntities);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const [hasInterestingFeatures, setHasInterestingFeaturs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isbn = bibs.getIn([metadataId, 'briefInfo', 'isbns', 0]);
  const profile = currentLibrary.getIn(['novelistParameters', 'profile']);
  const password = currentLibrary.getIn(['novelistParameters', 'password']);

  useEffect(() => {
    if (!(isbn && profile && password)) {
      return () => null;
    }

    const request = httpClient
      .get(NOVELIST_CONTENT_API_URL)
      .query({
        ClientIdentifier: isbn,
        ISBN: isbn,
        profile,
        password
      })
      .then(response => {
        if (request.aborted) {
          return;
        }

        const { FeatureContent: features = {} } = JSON.parse(response.text) ?? {};
        const interestingFeatures = Object.keys(features).filter(
          featureId => featureId !== 'EResourceRecommendation' && featureId !== 'GoodReads'
        );

        setHasInterestingFeaturs(interestingFeatures.length > 0);
      });

    return () => {
      request.aborted = true;
    };
  }, [isbn, profile, password]);

  const onOverlayOpen = useCallback(() => {
    function loadNoveListContent() {
      if (window.novSelect) {
        setIsLoading(true);
        window.novSelect.loadContentForQuery(
          {
            ClientIdentifier: isbn,
            ISBN: isbn,
            version: '2.3'
          },
          profile,
          password,
          () => setIsLoading(false)
        );
      } else {
        loadScript(NOVELIST_SCRIPT_SRC, loadNoveListContent);
      }
    }
    loadNoveListContent();
  }, [isbn, password, profile]);

  const renderOverlayContent = useCallback(() => {
    return (
      <>
        <div data-novelist-novelistselect={isbn} />
        {isLoading && <TableStencil />}
      </>
    );
  }, [isbn, isLoading]);

  if (!hasInterestingFeatures) {
    return null;
  }

  return (
    <MoreFromProviderContainer
      className={className}
      metadataId={metadataId}
      onOverlayOpen={onOverlayOpen}
      providerId={CONTENT_PROVIDER_IDS.NOVELIST}
      renderOverlayContent={renderOverlayContent}
      showHeading={showHeading}
    />
  );
};

MoreFromNoveListContainer.defaultProps = {
  className: '',
  showHeading: true
};

MoreFromNoveListContainer.propTypes = {
  className: PropTypes.string,
  metadataId: PropTypes.string.isRequired,
  showHeading: PropTypes.bool
};

export default MoreFromNoveListContainer;
